import { Button, ConfirmModal, TablePaginationContext } from '@/components';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { queryClient } from '@/constants';
import { EditAutomationModal } from '@/modules/Automations/pages/AutomationsPage/components/EditAutomationModal';
import { CreateRequestAutomationModal } from '@/modules/Automations/pages/RequestAutomation/components/CreateRequestAutomation';
import { useGetAutomationsByProcess } from '@/modules/Automations/queries';
import { useDeleteAutomation } from '@/modules/Automations/queries/delete-automation';
import { AutomationKey } from '@/modules/Automations/queries/keys';
import { useAssociatedAutomationsColumns } from '@/shared/hooks/useAssociatedAutomationsColumns';
import { ReactElement, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type AssociatedAutomationsTabProps = {
  processId: number;
};

export const AssociatedAutomationsTab = ({
  processId,
}: AssociatedAutomationsTabProps): ReactElement => {
  const { t } = useTranslation();
  const [automationSelected, setAutomationSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isRequestOpen, setIsRequestOpen] = useState(false);

  const { page, searchTerm, tableSort } = useContext(TablePaginationContext);

  const sort = tableSort[0];

  const { data: automations, isLoading } = useGetAutomationsByProcess(
    processId,
    page,
    searchTerm,
    sort?.columnId,
    sort?.direction,
  );

  const { mutate: deleteAutomation } = useDeleteAutomation();

  const handleOpenRemoveAutomationRowModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setAutomationSelected(id);
  }, []);

  const handleCloseRemoveAutomationRowModalState = useCallback(() => {
    setIsOpenDelete(false);
    setAutomationSelected(undefined);
  }, []);

  const handleOpenEditAutomationModalState = useCallback((id: number) => {
    setIsOpenEdit(true);
    setAutomationSelected(id);
  }, []);

  const handleCloseEditAutomationModalState = useCallback(() => {
    setIsOpenEdit(false);
    setAutomationSelected(undefined);
  }, []);

  const handleDeleteAutomation = useCallback(() => {
    if (!automationSelected) {
      return;
    }

    deleteAutomation(automationSelected, {
      onError: () => {
        toast.error(t('error_deleting_automation'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(AutomationKey.ALL);
        toast.success(t('success_deleting_automation'));
      },
    });

    handleCloseRemoveAutomationRowModalState();
  }, [
    automationSelected,
    deleteAutomation,
    handleCloseRemoveAutomationRowModalState,
    t,
  ]);

  const columns = useAssociatedAutomationsColumns({
    disableDeleteMessage: t('delete_automation'),
    onDeleteClick(row) {
      handleOpenRemoveAutomationRowModalState(row?.id as number);
    },
    onEditClick(row) {
      handleOpenEditAutomationModalState(row?.id as number);
    },
  });

  return (
    <>
      <PagedTable
        title={t('automations')}
        columns={columns}
        page={automations}
        isLoading={isLoading}
        emptyStateMessage={t('no_automations_available')}
        actions={
          <Button
            onClick={() => setIsRequestOpen(true)}
            variant="primary"
            className="px-5 py-2.5"
          >
            {t('request_new')}
          </Button>
        }
      />
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveAutomationRowModalState}
        title={t('please_confirm_action')}
        message={t('confirm_automation_delete')}
        onConfirm={handleDeleteAutomation}
      />
      {isRequestOpen && (
        <CreateRequestAutomationModal
          handleClose={() => setIsRequestOpen(false)}
          isOpen={isRequestOpen}
        />
      )}
      {isOpenEdit && automationSelected && (
        <EditAutomationModal
          isOpen={isOpenEdit}
          id={automationSelected}
          handleClose={handleCloseEditAutomationModalState}
        />
      )}
    </>
  );
};
